import type { ITenantDailyDistributionItem } from '@experiences/interfaces';
import { UiDialog } from '@experiences/ui-common';
import { useSnackbar } from '@experiences/util';
import {
    Checkbox,
    Divider,
    FormControlLabel,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import {
    FontVariantToken,
    Spacing,
} from '@uipath/apollo-core';
import {
    ApButton,
    ApRadioButton,
    ApRadioGroup,
    ApTypography,
} from '@uipath/portal-shell-react';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useIntl } from 'react-intl';

import { exportToCSV } from '../../../../common/utils/csvExport';
import { useConsumptionTimezone } from '../hooks/useConsumptionTimezone';
import { getConsumptionServiceDisplayName } from './ConsumptionByServices';

type TimeGranularity = 'day' | 'week' | 'month';

interface ConsumptionAnalyticsExportDialogProps {
    open: boolean;
    distributionData: ITenantDailyDistributionItem[];
    organizationName: string;
    tenantName: string;
    folderName?: string;
    folderPath?: string;
    viewType?: string;
    omitUnscopedColumns?: boolean;
    unitTypeDisplay: string;
    startDateUnix?: number;
    endDateUnix?: number;
    onClose: () => void;
    onExportTelemetry: (granularity: string, selectedServicesCount: number, totalServicesCount: number) => void;
}

const getWeekStartUtc = (date: Date): Date => {
    const weekStart = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
    const day = weekStart.getUTCDay();
    weekStart.setUTCDate(weekStart.getUTCDate() + (day === 0 ? -6 : 1 - day));
    return weekStart;
};

const useStyles = makeStyles(theme =>
    createStyles({
        content: {
            display: 'flex',
            flexDirection: 'column',
            gap: Spacing.SpacingBase,
        },
        section: {
            display: 'flex',
            flexDirection: 'column',
            gap: Spacing.SpacingXs,
        },
        sectionLabel: { color: theme.palette.semantic.colorForegroundDeEmp },
        serviceList: {
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '200px',
            overflowY: 'auto',
            paddingLeft: Spacing.SpacingXs,
        },
        checkboxLabel: {
            '& .MuiFormControlLabel-label': {
                fontSize: '14px',
                color: theme.palette.semantic.colorForeground,
            },
        },
    })
);

export const ConsumptionAnalyticsExportDialog: React.FC<ConsumptionAnalyticsExportDialogProps> = ({
    open,
    distributionData,
    organizationName,
    tenantName,
    folderName,
    folderPath,
    viewType,
    omitUnscopedColumns = false,
    unitTypeDisplay,
    startDateUnix,
    endDateUnix,
    onClose,
    onExportTelemetry,
}) => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const { enqueueSnackbar } = useSnackbar();
    const { timezoneId } = useConsumptionTimezone();

    const serviceList = useMemo(() => {
        const seen = new Set<string>();
        distributionData.forEach(item => item.services.forEach(s => seen.add(s.service)));
        return [ ...seen ].sort((a, b) => a.localeCompare(b));
    }, [ distributionData ]);

    const [ granularity, setGranularity ] = useState<TimeGranularity>('day');
    const [ selectedServices, setSelectedServices ] = useState<Set<string>>(
        () => new Set(serviceList)
    );

    useEffect(() => {
        if (open) {
            setGranularity('day');
            setSelectedServices(new Set(serviceList));
        }
    }, [ open, serviceList ]);

    const allServicesSelected = serviceList.length > 0 && selectedServices.size === serviceList.length;

    const toggleAllServices = useCallback(() => {
        if (serviceList.length === 0) {
            return;
        }
        setSelectedServices(prev =>
            prev.size === serviceList.length ? new Set() : new Set(serviceList)
        );
    }, [ serviceList ]);

    const toggleService = useCallback((service: string) => {
        setSelectedServices(prev => {
            const next = new Set(prev);
            next.has(service) ? next.delete(service) : next.add(service);
            return next;
        });
    }, []);

    const handleExport = useCallback(async () => {
        onExportTelemetry(granularity, selectedServices.size, serviceList.length);
        try {
            const exportDateUtc = new Date().toISOString();
            const safeTenantName = (tenantName || organizationName || 'export').replaceAll(/[\\/:*?"<>|]/g, '_');
            const startDateUtc = typeof startDateUnix === 'number' ? new Date(startDateUnix * 1000).toISOString() : '';
            const endDateUtc = typeof endDateUnix === 'number' ? new Date(endDateUnix * 1000).toISOString() : '';

            // Aggregate distribution data by the selected granularity
            const periodMap = new Map<string, Map<string, number>>();

            distributionData.forEach(item => {
                const itemDate = new Date(`${item.date}T00:00:00Z`);
                let periodKey: string;

                if (granularity === 'day') {
                    periodKey = item.date;
                } else if (granularity === 'week') {
                    periodKey = getWeekStartUtc(itemDate).toISOString()
                        .slice(0, 10);
                } else {
                    periodKey = `${itemDate.getUTCFullYear()}-${String(itemDate.getUTCMonth() + 1).padStart(2, '0')}`;
                }

                let row = periodMap.get(periodKey);
                if (!row) {
                    row = new Map();
                    periodMap.set(periodKey, row);
                }

                item.services
                    .filter(s => selectedServices.has(s.service))
                    .forEach(s => {
                        row.set(s.service, (row.get(s.service) ?? 0) + s.consumedAmount);
                    });
            });

            const sortedPeriods = [ ...periodMap.entries() ].sort(([ a ], [ b ]) => a.localeCompare(b));
            const services = [ ...selectedServices ].sort((a, b) => a.localeCompare(b));
            const periodColumnKeyMap: Record<TimeGranularity, string> = {
                day: 'date',
                week: 'week',
                month: 'month',
            };
            const periodColumnKey = periodColumnKeyMap[granularity];

            const toColumnKey = (service: string): string =>
                `${getConsumptionServiceDisplayName(service, translate).toLowerCase()
                    .replaceAll(/\s+/g, '_')}_consumption`;

            const csvRows = sortedPeriods.map(([ periodKey, consumption ]) => {
                const row: Record<string, string | number> = { [periodColumnKey]: periodKey };
                row.total_consumption = services.reduce((sum, service) => sum + (consumption.get(service) ?? 0), 0);
                services.forEach(service => {
                    row[toColumnKey(service)] = consumption.get(service) ?? 0;
                });
                row.unit = unitTypeDisplay;
                row.organization_name = organizationName;
                // Dashboards keep a fixed schema (blank when not applicable); Consumables V2 opts
                // out of the scope columns it has no value for.
                if (tenantName || !omitUnscopedColumns) {
                    row.tenant_name = tenantName;
                }
                if (folderName || !omitUnscopedColumns) {
                    row.folder = folderName ?? '';
                }
                if (folderPath || !omitUnscopedColumns) {
                    row.full_folder_path = folderPath ?? '';
                }
                if (viewType || !omitUnscopedColumns) {
                    row.view_type = viewType ?? '';
                }
                row.start_date = startDateUtc;
                row.end_date = endDateUtc;
                row.export_date_utc = exportDateUtc;
                row.time_zone = timezoneId;
                return row;
            });

            const safeFolderName = folderName?.replaceAll(/[\\/:*?"<>|\s]+/g, '_');
            const fileName = safeFolderName
                ? `ConsumptionAnalytics_CustomExport_${safeTenantName}_${safeFolderName}`
                : `ConsumptionAnalytics_CustomExport_${safeTenantName}`;
            await exportToCSV(csvRows, fileName);
            onClose();
        } catch {
            enqueueSnackbar(translate({ id: 'CLIENT_CONSUMPTION_DASHBOARD_EXPORT_ERROR' }), { variant: 'error' });
        }
    }, [
        distributionData,
        folderName,
        omitUnscopedColumns,
        folderPath,
        granularity,
        selectedServices,
        organizationName,
        tenantName,
        unitTypeDisplay,
        viewType,
        startDateUnix,
        endDateUnix,
        onClose,
        enqueueSnackbar,
        translate,
        onExportTelemetry,
        serviceList.length,
        timezoneId,
    ]);

    const dialogActions = useMemo(() => [
        <ApButton
            key="cancel"
            variant="tertiary"
            label={translate({ id: 'CLIENT_CANCEL' })}
            onClick={onClose}
        />,
        <ApButton
            key="export"
            variant="primary"
            data-cy="analytics-dialog-export-button"
            label={translate({ id: 'CLIENT_EXPORT' })}
            disabled={selectedServices.size === 0}
            onClick={handleExport}
        />,
    ], [ onClose, handleExport, selectedServices.size, translate ]);

    const granularityOptions: Array<{ value: TimeGranularity; labelId: string }> = [
        {
            value: 'day',
            labelId: 'CLIENT_CONSUMPTION_DASHBOARD_ANALYTICS_EXPORT_GRANULARITY_DAY',
        },
        {
            value: 'week',
            labelId: 'CLIENT_CONSUMPTION_DASHBOARD_ANALYTICS_EXPORT_GRANULARITY_WEEK',
        },
        {
            value: 'month',
            labelId: 'CLIENT_CONSUMPTION_DASHBOARD_ANALYTICS_EXPORT_GRANULARITY_MONTH',
        },
    ];

    return (
        <UiDialog
            title={translate({ id: 'CLIENT_CONSUMPTION_DASHBOARD_ANALYTICS_EXPORT_DIALOG_TITLE' })}
            close={onClose}
            actions={dialogActions}
            dialogProps={{
                open,
                maxWidth: 'xs',
                fullWidth: true,
            }}
        >
            <div className={classes.content}>

                {/* Time Granularity */}
                <div className={classes.section}>
                    <ApTypography
                        variant={FontVariantToken.fontSizeSBold}
                        className={classes.sectionLabel}>
                        {translate({ id: 'CLIENT_CONSUMPTION_DASHBOARD_ANALYTICS_EXPORT_GRANULARITY_LABEL' })}
                    </ApTypography>
                    <ApRadioGroup
                        value={granularity}
                        onValueChanged={(e) => setGranularity(e.detail as TimeGranularity)}
                        orientation="horizontal"
                        data-cy="granularity-radio-group"
                    >
                        {granularityOptions.map(opt => (
                            <ApRadioButton
                                key={opt.value}
                                value={opt.value}
                                label={translate({ id: opt.labelId })}
                                data-cy={`granularity-radio-${opt.value}`}
                            />
                        ))}
                    </ApRadioGroup>
                </div>

                {/* Services */}
                <div className={classes.section}>
                    <ApTypography
                        variant={FontVariantToken.fontSizeSBold}
                        className={classes.sectionLabel}>
                        {translate({ id: 'CLIENT_CONSUMPTION_DASHBOARD_ANALYTICS_EXPORT_SERVICES_LABEL' })}
                    </ApTypography>
                    <FormControlLabel
                        className={classes.checkboxLabel}
                        disabled={serviceList.length === 0}
                        control={
                            <Checkbox
                                size="small"
                                checked={allServicesSelected}
                                onChange={toggleAllServices}
                            />
                        }
                        label={translate({ id: 'CLIENT_CONSUMPTION_DASHBOARD_ANALYTICS_EXPORT_ALL_SERVICES' })}
                    />
                    <Divider />
                    <div className={classes.serviceList}>
                        {serviceList.map(service => (
                            <FormControlLabel
                                key={service}
                                className={classes.checkboxLabel}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={selectedServices.has(service)}
                                        onChange={() => toggleService(service)}
                                    />
                                }
                                label={getConsumptionServiceDisplayName(service, translate)}
                            />
                        ))}
                    </div>
                </div>

            </div>
        </UiDialog>
    );
};
